import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useMutation } from "react-query";
import { toast } from "react-toastify";
import {
  bulkUpdateVoters,
  deleteFullVoter,
  getAllBoothApi,
  getFullVoter,
} from "../../../services/voterService";
import { getCastProfession } from "../../../services/serviceCastPro";
import { getStorage } from "../../../helper/storage";
import { ROLE } from "../../../helper";
import UserContext from "../../../helper/UserContext";
import {
  getCachedVoters,
  patchCachedVoters,
  removeCachedVoter,
  setCachedVoters,
  subscribeVoterCache,
  voterCacheKey,
} from "./voterCache";
import {
  BOOTH_PAGE_SIZE,
  DEFAULT_PAGE_SIZE,
  FETCH_CONCURRENCY,
  FETCH_RETRIES,
  FILTER_DEBOUNCE_MS,
  LIST_LOAD_DEBOUNCE_MS,
  SERVER_MAX_PAGE_SIZE,
  filterVoters,
  getOptionValue,
  normalizeVoter,
  parseBoothOptions,
  uniqueOptions,
} from "./voterHelpers";

// Shared data layer for every Voter Management search page (Voter Search,
// Age Wise Search, Alphabetical Search). Ported verbatim from the old
// monolithic FullVoter.js (list fetch, booth-gated stats fetch, race-guard
// pattern, client-side colour/mobile-only re-filter, pagination) - see the
// comments below for the reasoning, unchanged from the original.
export function useVoterListFetch() {
  const resetPagination = {
    offset: 0,
    currentPage: 0,
    search: "",
    ordering: "",
  };

  const userLogin = getStorage("logged-in");
  const { user } = useContext(UserContext);

  const hasFetched = useRef(false);
  // Race-guard: each download captures a request id at start; if a newer
  // one has since started by the time it resolves, its result is dropped.
  const listRequestIdRef = useRef(0);

  const [status] = useState(1);
  const [filter, setFilter] = useState({
    limit: DEFAULT_PAGE_SIZE,
    status: 1,
    assembly_number:
      userLogin?.user_type !== ROLE.SUPER_ADMIN
        ? userLogin?.appDetails?.assembly_number?.[0]
        : user?.appDetails?.assembly_number?.[0],
    booth_number: "",
    app_name: userLogin?.app_name,
    party_name: userLogin?.party_name,
    ...resetPagination,
  });
  const [debouncedFilter, setDebouncedFilter] = useState(filter);

  const [formOrderName, setFormOrderName] = useState({});
  const [assamblyList, setAssamblyList] = useState([]);
  const [boothList, setBoothList] = useState([]);
  const [castList, setCastList] = useState([]);
  const [profList, setProfList] = useState([]);
  const [postList, setPostList] = useState([]);

  const [loaderForList, setLoaderForList] = useState(false);
  // 0-100 while loadVoterList is pulling the full list block by block.
  const [loadProgress, setLoadProgress] = useState(0);
  // const [sectionList, setSectionList] = useState([]); // unused - section filter isn't exposed in the UI

  const [hasMobileOnly, setHasMobileOnly] = useState(false);
  const [mobileOnlyPage, setMobileOnlyPage] = useState(0);

  const [showCastNo, setShowCastNo] = useState(false);
  const [showMobileNumbers, setShowMobileNumbers] = useState(false);

  const [selectedItems, setSelectedItems] = useState([]);

  const [openDel, setOpenDel] = useState(false);
  const [deleteId, setDeleteId] = useState("");
  const deleteIdRef = useRef(null);

  // --- Assembly / booth lists ---------------------------------------------

  useEffect(() => {
    if (user?.appDetails) {
      const rawAssemblyNumbers = user?.appDetails?.assembly_number;
      const useDetails = Array.isArray(rawAssemblyNumbers)
        ? rawAssemblyNumbers.map((i) => ({ label: i, value: i }))
        : [];
      if (useDetails.length) {
        setFormOrderName((i) => ({ ...i, assemblyFilter: useDetails[0] }));
        setFilter((s) => ({ ...s, assembly_number: useDetails[0].value }));
      }
      setAssamblyList(useDetails);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.appDetails]);

  useEffect(() => {
    const getBoothList = async (as_number) => {
      const res = await getAllBoothApi({ assembly_numbers: as_number });
      const details = parseBoothOptions(res);
      if (!details.length) console.warn("Booth list: no booth numbers found in response", res);
      setBoothList(details);
      if (details.length) {
        setFilter((current) => ({
          ...current,
          booth_number: details[0].value,
          limit: BOOTH_PAGE_SIZE,
          offset: 0,
          currentPage: 0,
        }));
        setFormOrderName((i) => ({ ...i, boothFilter: details[0] }));
      }
    };
    if (filter?.assembly_number) {
      getBoothList(filter.assembly_number);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter?.assembly_number]);

  useEffect(() => {
    const getCastAndProList = async () => {
      const res = await getCastProfession();
      const records = Array.isArray(res?.results) ? res.results : [];
      setCastList(
        uniqueOptions(records.map((item) => item?.caste_english ?? item?.caste ?? item?.cast))
      );
      setProfList(
        uniqueOptions(
          records.map((item) => item?.profession_english ?? item?.profession ?? item?.profession_name)
        )
      );
      setPostList(
        uniqueOptions(records.map((item) => item?.post_english ?? item?.post ?? item?.post_name))
      );
    };
    getCastAndProList();
  }, []);

  // Shared by the Booth dropdown and the "Clear Booth Address" banner button
  // so both take the exact same path back to "no booth selected".
  const handleBoothChange = useCallback((data) => {
    setFilter((s) => ({
      ...s,
      ...resetPagination,
      booth_number: data?.value,
      limit: data?.value ? BOOTH_PAGE_SIZE : DEFAULT_PAGE_SIZE,
    }));
    setFormOrderName((current) => ({ ...current, boothFilter: data }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAssemblyChange = useCallback((data) => {
    setFilter((s) => ({ ...s, ...resetPagination, assembly_number: data?.value }));
    setFormOrderName((current) => ({ ...current, assemblyFilter: data }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateListFilter = useCallback((values) => {
    setFilter((current) => ({ ...current, ...values, offset: 0, currentPage: 0 }));
  }, []);

  const handleClearAdvancedFilters = useCallback(() => {
    setHasMobileOnly(false);
    setFilter((current) => ({
      ...current,
      epic_number: "",
      first_name_e: "",
      last_name_e: "",
      mobile_no: "",
      voter_type: undefined,
      colour: undefined,
      voted: undefined,
      is_voted: undefined,
      cast: undefined,
      section_number: undefined,
      offset: 0,
      currentPage: 0,
    }));
    setFormOrderName((current) => ({
      ...current,
      colourFilter: null,
      votedFilter: null,
      voterTypeFilter: null,
      castFilter: null,
      sectionFilter: null,
    }));
  }, []);

  // --- Filters (debounced, applied client-side) ----------------------------

  useEffect(() => {
    const handler = setTimeout(() => setDebouncedFilter(filter), FILTER_DEBOUNCE_MS);
    return () => clearTimeout(handler);
  }, [filter]);

  // --- Voter list download (server) ------------------------------------------
  //
  // Only app/party/status/assembly (+ booth, see below) decide *which*
  // voters get downloaded. Every other filter - EPIC, names, mobile, voter
  // type, colour, voted, caste, "Has Mobile Number" - runs in the browser
  // over the downloaded list (filterVoters), so searching never refetches.
  // Downloaded lists live in voterCache, shared across pages.
  //
  // Booth selected: that booth's voters are always downloaded fresh from the
  // server (one quick request) every time the booth is selected or the page
  // opens - never served from a stored copy, so there's no Refresh button.
  // No booth selected: the whole assembly (the multi-minute load) is
  // downloaded once, kept in voterCache and reused, with a manual Refresh.
  const [, setCacheVersion] = useState(0);
  useEffect(() => subscribeVoterCache(() => setCacheVersion((n) => n + 1)), []);

  const assemblyKey = voterCacheKey(filter);
  const boothKey = filter?.booth_number ? voterCacheKey(filter, filter.booth_number) : null;
  const isBoothScope = !!boothKey;
  const wantedKey = isBoothScope ? boothKey : assemblyKey;
  const dataList = getCachedVoters(wantedKey) || null;

  const [loadError, setLoadError] = useState(null);
  const [lastLoadedAt, setLastLoadedAt] = useState(null);

  // The whole list behind a single loader. The backend caps one response at
  // SERVER_MAX_PAGE_SIZE rows, so the first request also returns the total
  // `count`, the remaining blocks are fetched FETCH_CONCURRENCY at a time, and
  // the list is stored once, complete and in order.
  const loadVoterList = useCallback(async (key, scope, { force = false } = {}) => {
    const cached = getCachedVoters(key);
    if (cached && !force) {
      setLoaderForList(false);
      setLoadError(null);
      setLastLoadedAt(cached.loadedAt);
      return;
    }
    const requestId = ++listRequestIdRef.current;
    const isStale = () => requestId !== listRequestIdRef.current;
    setLoaderForList(true);
    setLoadError(null);
    setLoadProgress(0);
    try {
      const fetchBlock = async (blockOffset, attempt = 0) => {
        try {
          const res = await getFullVoter({ ...scope, limit: SERVER_MAX_PAGE_SIZE, offset: blockOffset });
          if (res?.status !== 200) throw new Error("Bad response");
          return res;
        } catch (error) {
          if (attempt < FETCH_RETRIES && !isStale()) return fetchBlock(blockOffset, attempt + 1);
          throw error;
        }
      };

      const first = await fetchBlock(0);
      if (isStale()) return;
      const total = Number(first?.count || 0);
      const blocks = [Array.isArray(first?.results) ? first.results : []];

      const offsets = [];
      for (let o = SERVER_MAX_PAGE_SIZE; o < total; o += SERVER_MAX_PAGE_SIZE) offsets.push(o);
      const blockCount = offsets.length + 1;
      let done = 1;
      setLoadProgress(Math.round((done / blockCount) * 100));

      for (let i = 0; i < offsets.length; i += FETCH_CONCURRENCY) {
        const batch = offsets.slice(i, i + FETCH_CONCURRENCY);
        const responses = await Promise.all(batch.map((o) => fetchBlock(o)));
        if (isStale()) return;
        responses.forEach((res) => blocks.push(Array.isArray(res?.results) ? res.results : []));
        done += batch.length;
        setLoadProgress(Math.round((done / blockCount) * 100));
      }

      const results = blocks.flat().map(normalizeVoter);
      const loadedAt = Date.now();
      setCachedVoters(key, { count: results.length, results, loadedAt });
      setLastLoadedAt(loadedAt);
      setCacheVersion((n) => n + 1);
    } catch (error) {
      if (isStale()) return;
      console.error("Error fetching voter list:", error);
      setLoadError("Couldn't load the voter list");
      toast.error("Couldn't load the voter list. Please try again.");
    } finally {
      if (!isStale()) setLoaderForList(false);
    }
  }, []);

  const scopeFor = useCallback(
    (key) => {
      const { app_name, party_name, status: listStatus, assembly_number } = filter || {};
      return {
        app_name,
        party_name,
        status: listStatus,
        assembly_number,
        ...(key === boothKey && key !== assemblyKey ? { booth_number: filter.booth_number } : {}),
      };
    },
    [filter, boothKey, assemblyKey]
  );

  // Debounced like the old list fetch, so the initial "assembly, then its
  // first booth gets auto-selected" sequence doesn't start a whole-assembly
  // download that's immediately thrown away.
  useEffect(() => {
    if (!filter?.app_name || !filter?.party_name) {
      if (!hasFetched.current) {
        toast?.error("App Name and Party Name Required");
        hasFetched.current = true;
      }
      return undefined;
    }
    // Booth: always a fresh request (force). Assembly: reuse when stored.
    if (isBoothScope) {
      setLoaderForList(true);
      const handler = setTimeout(
        () => loadVoterList(wantedKey, scopeFor(wantedKey), { force: true }),
        LIST_LOAD_DEBOUNCE_MS
      );
      return () => clearTimeout(handler);
    }
    if (getCachedVoters(wantedKey)) {
      loadVoterList(wantedKey, scopeFor(wantedKey));
      return undefined;
    }
    setLoaderForList(true);
    const handler = setTimeout(() => loadVoterList(wantedKey, scopeFor(wantedKey)), LIST_LOAD_DEBOUNCE_MS);
    return () => clearTimeout(handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wantedKey]);

  // Manual "Refresh" - re-downloads the list currently in use.
  const refreshVoterList = useCallback(
    () => loadVoterList(wantedKey, scopeFor(wantedKey), { force: true }),
    [loadVoterList, wantedKey, scopeFor]
  );

  // --- Client-side filtering + stats ------------------------------------------

  const filteredVoters = useMemo(
    () =>
      filterVoters(Array.isArray(dataList?.results) ? dataList.results : [], {
        ...debouncedFilter,
        // A booth list is already that booth only (server-filtered).
        booth_number: isBoothScope ? undefined : debouncedFilter?.booth_number,
        hasMobileOnly,
      }),
    [dataList, debouncedFilter, hasMobileOnly, isBoothScope]
  );

  // Age Wise / Alphabetical Search read the booth's voters from statsList.
  const statsList = useMemo(
    () => (debouncedFilter?.booth_number && dataList ? { results: filteredVoters } : null),
    [debouncedFilter?.booth_number, dataList, filteredVoters]
  );
  const isStatsLoading = loaderForList;
  const statsError = loadError;
  const retryStats = refreshVoterList;

  const voterStats = useMemo(() => {
    const total = filteredVoters.length;
    const voted = filteredVoters.filter((voter) => voter.is_voted === true).length;
    const supporter = filteredVoters.filter((voter) => Number(voter.colour) === 1).length; // Green
    const neutral = filteredVoters.filter((voter) => Number(voter.colour) === 3).length; // Yellow
    const opposition = filteredVoters.filter((voter) => Number(voter.colour) === 2).length; // Red
    // Same gender-detection regex the PDF export (VoterSearch.js) already
    // uses, so the stats bar and the exported roll agree on the same counts.
    const male = filteredVoters.filter((voter) => /^m/i.test(voter.gender_e || voter.gender_h || "")).length;
    const female = filteredVoters.filter((voter) => /^f|^w/i.test(voter.gender_e || voter.gender_h || "")).length;
    return {
      total,
      voted,
      notVoted: total - voted,
      supporter,
      neutral,
      opposition,
      // Anything else - colour=4 (Blue), the API's -1 sentinel, null/undefined -
      // counts as not assigned a canvassing colour at all.
      notAssigned: total - supporter - neutral - opposition,
      male,
      female,
    };
  }, [filteredVoters]);

  // No pagination: the table shows every matching voter (virtualized).
  const pageVoters = filteredVoters;
  const activeVoterTotal = filteredVoters.length;
  const loadedVoterTotal = Number(dataList?.count || 0);

  const boothAddress =
    (Array.isArray(statsList?.results) && statsList.results[0]?.booth_address_e) ||
    (Array.isArray(dataList?.results) && dataList.results[0]?.booth_address_e) ||
    "";

  // --- Selection -----------------------------------------------------------

  const handleCheckboxChange = useCallback((item) => {
    setSelectedItems((prev) => {
      const isChecked = prev.some((selected) => selected.id === item.id);
      if (isChecked) return prev.filter((selected) => selected.id !== item.id);
      return [...prev, item];
    });
  }, []);

  const selectedIdSet = useMemo(
    () => new Set(selectedItems.map((item) => item.id)),
    [selectedItems]
  );
  const selectedOnPageCount = useMemo(
    () => pageVoters.filter((item) => selectedIdSet.has(item.id)).length,
    [pageVoters, selectedIdSet]
  );
  const allPageVotersSelected =
    pageVoters.length > 0 && selectedOnPageCount === pageVoters.length;

  const handleSelectAll = useCallback(() => {
    setSelectedItems((current) => {
      const pageIds = new Set(pageVoters.map((item) => item.id));
      const selectedPageCount = current.filter((item) => pageIds.has(item.id)).length;
      if (selectedPageCount === pageVoters.length) {
        return current.filter((item) => !pageIds.has(item.id));
      }
      const existingIds = new Set(current.map((item) => item.id));
      return [...current, ...pageVoters.filter((item) => !existingIds.has(item.id))];
    });
  }, [pageVoters]);

  // --- Pagination (disabled: the whole list now loads in one request) -----

  // const handlePageClick = useCallback(
  //   (data) => {
  //     const nextPage = data.selected;
  //     if (clientFilterActive) {
  //       setMobileOnlyPage(nextPage);
  //       return;
  //     }
  //     setFilter((s) => ({ ...s, offset: nextPage * s.limit, currentPage: nextPage }));
  //   },
  //   [clientFilterActive]
  // );

  // const showPagination = clientFilterActive
  //   ? voterStats.total > filter.limit
  //   : Array.isArray(dataList?.results) &&
  //     dataList.results.length > 0 &&
  //     Number(dataList?.count) > dataList.results.length;

  // const pageCount = Math.max(Math.ceil(activeVoterTotal / (filter?.limit || DEFAULT_PAGE_SIZE)), 0);
  // const paginationStart = activeVoterTotal === 0 ? 0 : currentPageIndex * filter.limit + 1;
  // const paginationEnd = Math.min((currentPageIndex + 1) * filter.limit, activeVoterTotal);

  // --- Single-row delete (trash icon) --------------------------------------

  const onDeleteSuccess = useCallback(
    (message, id) => {
      toast.success(message || "success");
      removeCachedVoter(id);
      setSelectedItems((current) => current.filter((item) => item.id !== id));
      setOpenDel(false);
    },
    []
  );

  const deleteMutation = useMutation(deleteFullVoter, {
    onSuccess: (res) => {
      if (res?.success) onDeleteSuccess(res?.message, deleteIdRef.current);
    },
  });

  const hendleDeleteTag = useCallback((data, flag) => {
    setOpenDel(flag);
    setDeleteId(data);
  }, []);

  const handleDelete = useCallback(() => {
    deleteIdRef.current = deleteId?.id;
    deleteMutation.mutate({
      id: deleteId?.id,
      app_name: deleteId?.app_name,
      party_name: deleteId?.party_name,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deleteId]);

  // --- Bulk update (Voter Search's toolbar action) --------------------------

  const bulkUpdateMutation = useMutation(bulkUpdateVoters, {
    onSuccess: (res) => {
      if (res?.success) {
        toast.success(res?.message || "Voters updated successfully");
        setSelectedItems([]);
      } else {
        toast.error(res?.message || "Bulk update failed");
      }
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message || "Bulk update failed");
    },
  });

  const bulkUpdateSubmit = useCallback(
    (bulkFields, bulkForm, { onSuccess, onSettled } = {}) => {
      const votersPayload = selectedItems.map((voter) => {
        const record = { epic_no: voter.epic_no };
        Object.keys(bulkFields).forEach((field) => {
          if (bulkFields[field]) record[field] = getOptionValue(bulkForm[field]);
        });
        return record;
      });
      bulkUpdateMutation.mutate(
        {
          app_name: userLogin?.app_name,
          party_name: userLogin?.party_name,
          voters: votersPayload,
        },
        {
          onSuccess: (res) => {
            if (!res?.success) return;
            // Apply the same field changes to the downloaded list.
            const patchByEpic = new Map(
              votersPayload.map(({ epic_no, ...fields }) => [epic_no, fields])
            );
            patchCachedVoters((voter) => patchByEpic.get(voter.epic_no) || null);
            onSuccess?.();
          },
          onSettled,
        }
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedItems, userLogin?.app_name, userLogin?.party_name]
  );

  return {
    userLogin,
    filter,
    setFilter,
    debouncedFilter,
    updateListFilter,
    handleClearAdvancedFilters,
    status,

    formOrderName,
    setFormOrderName,
    assamblyList,
    boothList,
    castList,
    profList,
    postList,
    handleAssemblyChange,
    handleBoothChange,

    dataList,
    loaderForList,
    loadProgress,
    loadError,
    lastLoadedAt,
    refreshVoterList,
    loadedVoterTotal,
    isBoothScope,

    hasMobileOnly,
    setHasMobileOnly,
    mobileOnlyPage,
    setMobileOnlyPage,
    statsList,
    isStatsLoading,
    statsError,
    retryStats,
    voterStats,

    showCastNo,
    setShowCastNo,
    showMobileNumbers,
    setShowMobileNumbers,

    pageVoters,
    activeVoterTotal,
    boothAddress,

    selectedItems,
    setSelectedItems,
    selectedIdSet,
    selectedOnPageCount,
    allPageVotersSelected,
    handleCheckboxChange,
    handleSelectAll,

    openDel,
    setOpenDel,
    hendleDeleteTag,
    handleDelete,

    bulkUpdateSubmit,
  };
}
